import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  addDoc,
  setDoc,
  serverTimestamp,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { MessageSquare, Send, Clock } from 'lucide-react';

export default function SupportPage() {
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setTickets([]);
      setLoadingTickets(false);
      return;
    }

    const ticketsQuery = query(
      collection(db, 'users', user.uid, 'supportTickets'),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      ticketsQuery,
      snapshot => {
        const items =
          snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) || [];
        setTickets(items);
        setLoadingTickets(false);
      },
      error => {
        console.error('Error loading support tickets:', error);
        setLoadingTickets(false);
      },
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const handleSubmit = async () => {
    if (!user?.uid) {
      alert(
        t('profile.supportLoginRequired', {
          defaultValue: 'You need to be logged in to contact support.',
        }),
      );
      return;
    }

    if (!subject.trim() || !message.trim()) {
      alert(
        t('profile.supportRequired', {
          defaultValue: 'Please enter a subject and message.',
        }),
      );
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        userId: user.uid,
        userEmail: user.email || null,
        subject: subject.trim(),
        message: message.trim(),
        status: 'open',
        createdAt: serverTimestamp(),
      };

      // Root collection for admin panel
      const ticketRef = await addDoc(collection(db, 'supportTickets'), payload);

      // Mirror under user document for user view with same ID
      await setDoc(
        doc(db, 'users', user.uid, 'supportTickets', ticketRef.id),
        payload,
      );

      setSubject('');
      setMessage('');

      alert(
        t('profile.supportSent', {
          defaultValue:
            'Your message has been sent. We will get back to you soon.',
        }),
      );
    } catch (error) {
      console.error('Error sending support ticket:', error);
      alert(
        t('profile.supportFailed', {
          defaultValue: 'Failed to send support message. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Group tickets so that follow-ups appear under a single main topic per subject
  const normalizeSubject = subject =>
    (subject || '').replace(/^Re:\s*/i, '').trim();

  const groupedBySubject = tickets.reduce((acc, t) => {
    const key = normalizeSubject(t.subject);
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  const rootTickets = [];
  const repliesByRootId = {};

  Object.values(groupedBySubject).forEach(group => {
    // Prefer document without parentTicketId as root, otherwise first by createdAt
    const rootCandidate =
      group.find(t => !t.parentTicketId) ||
      group.slice().sort((a, b) => {
        const aTime = a.createdAt?.toDate
          ? a.createdAt.toDate().getTime()
          : 0;
        const bTime = b.createdAt?.toDate
          ? b.createdAt.toDate().getTime()
          : 0;
        return aTime - bTime;
      })[0];

    rootTickets.push(rootCandidate);

    const replies = group.filter(t => t.id !== rootCandidate.id);
    if (replies.length) {
      repliesByRootId[rootCandidate.id] = replies;
    }
  });

  const formatDate = date => {
    if (!date) return '';
    try {
      const dateObj = date?.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleString();
    } catch (e) {
      return '';
    }
  };

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-slate-100 mb-4">
              {t('profile.supportTitle') || 'Support'}
            </h1>
            <p className="text-gray-600 dark:text-slate-300 mb-6">
              {t('profile.supportLoginRequired', {
                defaultValue: 'You need to be logged in to contact support.',
              })}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center justify-center rounded-2xl bg-black px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('incomingRequests.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="h-8 w-8 text-yellow-500" />
            <h1 className="text-4xl font-black text-gray-900 dark:text-white">
              {t('profile.supportTitle') || 'Support'}
            </h1>
          </div>
          <p className="text-gray-600 dark:text-slate-400">
            {t('profile.supportSubtitle', {
              defaultValue:
                'Tell us briefly what you need help with and our team will respond as soon as possible.',
            })}
          </p>
        </div>

        {/* New Ticket Form */}
        <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8 mb-8">
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">
            {t('profile.supportNewTicket', {
              defaultValue: 'Create New Support Request',
            })}
          </h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                {t('profile.supportSubject') || 'Subject'}
              </label>
              <input
                type="text"
                placeholder={
                  t('profile.supportSubjectPlaceholder') ||
                  'Short title for your issue'
                }
                value={subject}
                onChange={e => setSubject(e.target.value)}
                disabled={submitting}
                className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                {t('profile.supportMessage') || 'Message'}
              </label>
              <textarea
                rows={6}
                placeholder={
                  t('profile.supportMessagePlaceholder') ||
                  'Describe your question or issue in detail'
                }
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={submitting}
                className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 resize-none"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !subject.trim() || !message.trim()}
              className="w-full rounded-xl bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-black font-bold py-3 px-6 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Clock className="h-5 w-5 animate-spin" />
                  {t('profile.supportSending') || 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  {t('profile.supportSend') || 'Send message'}
                </>
              )}
            </button>
          </div>
        </section>

        {/* Previous Tickets */}
        {user?.uid && (
          <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8">
            <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">
              {t('profile.supportHistoryTitle', {
                defaultValue: 'Your previous requests',
              })}
            </h2>

            {loadingTickets ? (
              <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                {t('profile.supportHistoryLoading', {
                  defaultValue: 'Loading...',
                })}
              </div>
            ) : rootTickets.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                {t('profile.supportHistoryEmpty', {
                  defaultValue:
                    'You have not created any support requests yet.',
                })}
              </div>
            ) : (
              <div className="space-y-4">
                {rootTickets.map(ticket => {
                  const createdAtLabel = formatDate(ticket.createdAt);
                  const replyCount = repliesByRootId[ticket.id]?.length || 0;

                  return (
                    <button
                      key={ticket.id}
                      onClick={() =>
                        navigate(`/profile/support/${ticket.id}`)
                      }
                      className="w-full text-left rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 hover:border-yellow-400 dark:hover:border-yellow-500 transition"
                    >
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex-1">
                          {ticket.subject || '-'}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                            ticket.status === 'answered'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                          }`}
                        >
                          {ticket.status || 'open'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-slate-400 mb-2 line-clamp-2">
                        {ticket.message}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-slate-500">
                        {createdAtLabel && <span>{createdAtLabel}</span>}
                        {replyCount > 0 && (
                          <span className="font-semibold">
                            {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

